import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UserPlus,
  Search,
  Filter,
  ChevronRight,
  Sparkles,
  Trash2,
  Edit3,
  ClipboardCheck,
  UserCheck,
  Clock,
  X,
  Check,
  AlertTriangle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  proposto: { label: "Proposto", color: "text-sky-300", bg: "bg-sky-500/10", border: "border-sky-500/30" },
  sindicancia: { label: "Sindicância", color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  aprovado: { label: "Aprovado", color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  rejeitado: { label: "Rejeitado", color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  iniciado: { label: "Iniciado ✧", color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/10", border: "border-[#D4AF37]/40" },
};

export const CandidatesView = ({ members = [] }) => {
  const [candidates, setCandidates] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCand, setEditingCand] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status_filter=${statusFilter}` : "";
      const res = await axios.get(`${API}/candidatos${params}`);
      setCandidates(res.data);
    } catch (err) {
      toast.error("Erro ao carregar candidatos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line
  }, [statusFilter]);

  const handleInitiate = async (cand) => {
    if (!window.confirm(`Confirmar iniciação de ${cand.name} como Aprendiz Maçom?`)) return;
    try {
      const res = await axios.post(`${API}/candidatos/${cand.id}/iniciar`);
      toast.success(res.data.message);
      fetchCandidates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar candidato.");
    }
  };

  const handleDelete = async (cand) => {
    if (!window.confirm(`Excluir a proposta de ${cand.name}?`)) return;
    try {
      await axios.delete(`${API}/candidatos/${cand.id}`);
      toast.success("Candidato excluído.");
      fetchCandidates();
    } catch (err) {
      toast.error("Erro ao excluir.");
    }
  };

  const filtered = candidates.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) || (c.email || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="candidates-view">
      {/* Header */}
      <div className="luxury-card p-6 border-l-4 border-l-sky-400">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-xs uppercase tracking-wider text-sky-400 font-bold mb-1">
              <UserPlus className="w-4 h-4" />
              <span>Secretaria • Processo Iniciático</span>
            </div>
            <h2 className="text-2xl font-serif font-bold text-white">Candidatos à Iniciação</h2>
            <p className="text-xs text-slate-400 mt-1">
              Acompanhe propostas, sindicâncias, aprovações e iniciações no Aprendizado.
            </p>
          </div>
          <button
            data-testid="candidates-btn-new"
            onClick={() => {
              setEditingCand(null);
              setShowModal(true);
            }}
            className="flex items-center space-x-2 bg-sky-500 hover:bg-sky-600 text-white font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(56,189,248,0.25)]"
          >
            <UserPlus className="w-4 h-4" />
            <span>Nova Proposta</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mt-5">
          <div className="md:col-span-3 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="candidates-input-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou email..."
              className="w-full bg-[#0B111E] border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:border-sky-400 outline-none"
            />
          </div>
          <select
            data-testid="candidates-select-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="md:col-span-3 bg-[#0B111E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-sky-400 outline-none"
          >
            <option value="all">Todos os status</option>
            <option value="proposto">Propostos</option>
            <option value="sindicancia">Em sindicância</option>
            <option value="aprovado">Aprovados (aptos à iniciação)</option>
            <option value="rejeitado">Rejeitados</option>
            <option value="iniciado">Já iniciados</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="luxury-card p-0 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 bg-[#080D18] text-left text-[11px] uppercase tracking-wider text-slate-400">
              <th className="py-3 px-4">Candidato</th>
              <th className="py-3 px-4">Proposta</th>
              <th className="py-3 px-4">Proposto por</th>
              <th className="py-3 px-4">Síndico</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-slate-500">
                  Carregando...
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-slate-500 text-sm">
                  <UserPlus className="w-8 h-8 mx-auto mb-3 opacity-40" />
                  Nenhum candidato cadastrado ainda. Clique em "Nova Proposta" para começar.
                </td>
              </tr>
            )}
            {filtered.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.proposto;
              const proposer = members.find((m) => m.id === c.proposed_by_id);
              const sindic = members.find((m) => m.id === c.sindico_id);
              return (
                <tr
                  key={c.id}
                  data-testid={`candidate-row-${c.id}`}
                  className="border-b border-slate-800/60 hover:bg-white/5 transition-colors"
                >
                  <td className="py-3 px-4">
                    <div className="font-semibold text-white">{c.name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{c.email} • {c.phone}</div>
                    {c.profession && <div className="text-[10px] text-slate-500 mt-0.5">{c.profession}</div>}
                  </td>
                  <td className="py-3 px-4 text-slate-300 font-mono text-xs">{c.proposal_date || "—"}</td>
                  <td className="py-3 px-4 text-slate-300 text-xs">{proposer?.name || "—"}</td>
                  <td className="py-3 px-4 text-slate-300 text-xs">{sindic?.name || "—"}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] uppercase font-bold px-2.5 py-1 rounded-full border ${meta.color} ${meta.bg} ${meta.border}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right space-x-1">
                    {c.status === "aprovado" && (
                      <button
                        data-testid={`candidate-btn-initiate-${c.id}`}
                        onClick={() => handleInitiate(c)}
                        title="Iniciar como Aprendiz Maçom"
                        className="inline-flex items-center space-x-1 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-2.5 py-1.5 rounded-lg text-[10px] uppercase tracking-wider"
                      >
                        <Sparkles className="w-3 h-3" />
                        <span>Iniciar</span>
                      </button>
                    )}
                    <button
                      data-testid={`candidate-btn-edit-${c.id}`}
                      onClick={() => {
                        setEditingCand(c);
                        setShowModal(true);
                      }}
                      className="inline-flex p-1.5 rounded-lg text-slate-400 hover:text-sky-400 hover:bg-sky-500/10"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      data-testid={`candidate-btn-delete-${c.id}`}
                      onClick={() => handleDelete(c)}
                      className="inline-flex p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <CandidateModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingCand(null);
          }}
          onSaved={() => {
            setShowModal(false);
            setEditingCand(null);
            fetchCandidates();
          }}
          initialData={editingCand}
          members={members}
        />
      )}
    </div>
  );
};

const CandidateModal = ({ isOpen, onClose, onSaved, initialData, members }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    birth_date: "",
    profession: "",
    address: "",
    proposed_by_id: "",
    sindico_id: "",
    status: "proposto",
    proposal_date: new Date().toISOString().slice(0, 10),
    sindicancia_report: "",
    notes: "",
  });

  useEffect(() => {
    if (initialData) {
      setForm({ ...form, ...initialData });
    } else {
      setForm((f) => ({ ...f, name: "", email: "", phone: "", proposal_date: new Date().toISOString().slice(0, 10) }));
    }
    // eslint-disable-next-line
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (initialData?.id) {
        await axios.put(`${API}/candidatos/${initialData.id}`, form);
        toast.success("Candidato atualizado.");
      } else {
        await axios.post(`${API}/candidatos`, form);
        toast.success("Proposta registrada com sucesso.");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="candidate-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] bg-[#0B111E] border border-sky-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-slate-800 bg-[#080D18] flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-sky-400 font-bold flex items-center space-x-2">
              <UserPlus className="w-3.5 h-3.5" />
              <span>Secretaria • {initialData ? "Editar" : "Nova"} Proposta</span>
            </div>
            <h2 className="text-lg font-serif font-bold text-white mt-1">
              {initialData ? initialData.name : "Novo Candidato à Iniciação"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldInput label="Nome completo" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="cand-input-name" />
            <FieldInput label="E-mail" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="cand-input-email" />
            <FieldInput label="Telefone" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="cand-input-phone" />
            <FieldInput label="Data de nascimento" type="date" value={form.birth_date || ""} onChange={(v) => setForm({ ...form, birth_date: v })} testid="cand-input-birth" />
            <FieldInput label="Profissão" value={form.profession || ""} onChange={(v) => setForm({ ...form, profession: v })} testid="cand-input-profession" />
            <FieldInput label="Data da proposta" type="date" value={form.proposal_date || ""} onChange={(v) => setForm({ ...form, proposal_date: v })} testid="cand-input-proposal-date" />
          </div>

          <FieldInput label="Endereço completo" value={form.address || ""} onChange={(v) => setForm({ ...form, address: v })} testid="cand-input-address" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldSelect
              label="Proposto por (Irmão)"
              value={form.proposed_by_id || ""}
              onChange={(v) => setForm({ ...form, proposed_by_id: v })}
              options={[{ value: "", label: "— Selecionar —" }, ...members.map((m) => ({ value: m.id, label: m.name }))]}
              testid="cand-select-proposer"
            />
            <FieldSelect
              label="Síndico"
              value={form.sindico_id || ""}
              onChange={(v) => setForm({ ...form, sindico_id: v })}
              options={[{ value: "", label: "— Selecionar —" }, ...members.map((m) => ({ value: m.id, label: m.name }))]}
              testid="cand-select-sindico"
            />
          </div>

          <FieldSelect
            label="Status do processo"
            value={form.status}
            onChange={(v) => setForm({ ...form, status: v })}
            options={[
              { value: "proposto", label: "Proposto" },
              { value: "sindicancia", label: "Em sindicância" },
              { value: "aprovado", label: "Aprovado (apto à iniciação)" },
              { value: "rejeitado", label: "Rejeitado" },
            ]}
            testid="cand-select-status"
          />

          <FieldTextarea
            label="Relatório de sindicância"
            value={form.sindicancia_report || ""}
            onChange={(v) => setForm({ ...form, sindicancia_report: v })}
            rows={3}
            testid="cand-input-report"
          />

          <FieldTextarea
            label="Observações"
            value={form.notes || ""}
            onChange={(v) => setForm({ ...form, notes: v })}
            rows={2}
            testid="cand-input-notes"
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-wider text-slate-400 hover:text-white font-semibold px-4"
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="cand-btn-submit"
              className="flex items-center space-x-2 bg-sky-500 hover:bg-sky-600 text-white font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider"
            >
              <Check className="w-3.5 h-3.5" />
              <span>{initialData ? "Atualizar" : "Registrar"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FieldInput = ({ label, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">{label}{required && " *"}</label>
    <input
      data-testid={testid}
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-sky-400 outline-none"
    />
  </div>
);

const FieldSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">{label}</label>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-sky-400 outline-none"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const FieldTextarea = ({ label, value, onChange, rows = 3, testid }) => (
  <div>
    <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">{label}</label>
    <textarea
      data-testid={testid}
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-sky-400 outline-none resize-none"
    />
  </div>
);
